import { LoanApplicationEntities } from 'commonui';

export interface TenantBranding {
    cnxStorefrontSubHeader?: string;
    cnxStorefrontDisclosuresFooter?: string;
    cnxTermsAndConditionsPreApproval?: string;
    cnxTermsAndConditionsInvitationToApply?: string;
    cnxOfferTheme: TenantBrandingOfferTheme;
}

export interface TenantBrandingOfferTheme {
    brandingOptionId: number;
    specificationJson: string;
}

export interface TenantBrandingOption {
    brandingOptionId: number;
    tenantPropertyName: string;
    optionLabel: string;
    specificationJson: string;
}

export interface CoreProduct {
    id: string;
    tenantId: string;
    templateId: string;
    recordState: string;
    name: string;
    description: string;
    category: string;
    type: string;
    subtype: string;
    version: number;
    versionDescription: string;
    startDate: Date;
    endDate: Date;
    applicationIdleDuration: number;
    applicationExpiryDuration: number;
    allowSimulation: boolean;
    primaryGroupValue: string;
    secondaryGroupValue: string | null;
    latestRateScheduleDate?: Date;
    metadataJSON: string;
    businessRulesJSON: string;
    specificationJSON: string;
    previousVersionProductId: string;
    mediaUrl?: string;
    lastUpdated: Date;
}

export interface LifeQuoteCoverageOption {
    productCode: string;
    coverage: number;
    premium: {
        amount: number;
        frequency: string;
    }[];
}

export interface LifeQuoteProduct {
    productCode: string; // Product Code for product being offered.
    displayName: string; // example: 'SIT'
    productType: string; // example: 'Term'
    productCategory: string; // example: 'Simplified Issue Term'
    lineOfBusiness: string; // example: 'Life'
    productDescription: string; // example: 'SI Term to Age 80'
    coverageOptions: LifeQuoteCoverageOption[];
    billingOptions: {
        billingMethodCode: string;
        billingFrequencies: string[];
    }[];
    errors: { code: string; description: string }[];
    isApplyOnline: boolean;
    riders?: LifeQuoteProductRider[];
    systemProductCode: string;
    tieredCoverageOptions?: LifeQuoteProductTieredCoverageOption[];
    underwritingClassCode: string; // PT, PN, ST, SN, N, P, T, S
    underwritingClassDescription: string; // Standard Non-Tobacco, Standard Tobacco, Preferred Non-Tobacco, Preferred Tobacco
}

export interface LifeQuoteProductRider {
    isEligible: boolean;
    coverageOptions: LifeQuoteCoverageOption[];
    minimumIssueAge?: number;
    maximumIssueAge?: number;
    minimumCoverageAmount?: number;
    maximumCoverageAmount?: number;
    riderCode: string;
    riderDescription: string;
    riderType: string;
    learnMoreModalContent?: string;
    crossSellModuleTitle?: string;
    tieredCoverageOptions?: LifeQuoteProductTieredCoverageOption[];
    underwritingClassCode: string; // PT, PN, ST, SN, N, P, T, S
    underwritingClassDescription: string; // Standard, Preferred, Standard Tobacco, Standard Non-Tobacco, Preferred Tobacco, Preferred Non-Tobacco,
}

export interface LifeQuoteProductTieredCoverageOption {
    underwritingClassCode: string; // PT, PN, ST, SN, N, P, T, S
    underwritingClassDescription: string; // Standard Non-Tobacco, Standard Tobacco, Preferred Non-Tobacco, Preferred Tobacco
    coverageOptions: LifeQuoteCoverageOption[];
}

export interface LifeQuoteRequest {
    serviceProvider?: string; // Service Provider to use (optional). Defaults to 'TruStage', the only option at the moment.
    insuranceProductCode?: string; // Product Code to quote (optional).
    coreProductFormId?: string;
    coreProductId?: string;
    contractNumber?: string; // TruStage contract number of credit union to attribute application to (require, but can be blank).
    partnerCode?: string;
    organizationId?: string; // (require, but can be blank)
    requestingSystemName?: string;
    channel?: string; // Channel applied thru (use 'IONLN' not 'WEB') (optional, defaults to 'IONLN').
    stateAbbr?: string; // two letter state abbreviation (required if no zip code provided).
    zipCode?: string; // 5 digit zip code (required if no state abbreviation provided).
    dateOfBirth: string; // date of birth [YYY-MM-DD] (required).
    sexAtBirth: string; // sex at birth ['M' for male, 'F' for female] (required).
    tobaccoUse?: boolean; // does the applicant use tobacco products [Yes/No] (optional).
    coverageInterval?: number;
    minCoverage?: number;
    maxCoverage?: number;
    billingMethod?: number;
}

export interface LifeQuoteResponse {
    quotes: LifeQuoteProduct[];
}

export interface InsuranceStartAppRequest {
    productCode: string;
    coverage: number;
    stateAbbr: string;
    dateOfBirth: string;
    sexAtBirth: string;
}

export interface IInsuranceStartAppResponse {
    applicationId: string;
    url: string;
    sessionId?: string;
}

export interface InsuranceAppQuoteResponse {
    appId: string;
    minimumApplicationCoverage?: number;
    maximumApplicationCoverage?: number;
    quote: LifeQuoteProduct;
}

export interface InsuranceAppCancelResponse {
    insuranceAppId: string;
    appResult: {
        code: number;
        redirect: boolean;
        redirectUrl?: string;
    };
}

export interface IDataFieldValue {
    fieldCode: string;
    value: string;
}

export interface SubmitLoanAppRequestBody {
    dataFieldValues?: IDataFieldValue[];
}

export interface ITenantDetails {
    legalName: string;
    abbreviatedName: string;
    website: string;
    id: string;
    resourceUrl: string;
    contractNumber: string;
    coreBankingProvider: 'SYMITAR' | 'FISERV' | 'Other';
}

export interface ITenantBranding {
    altLogo?: string;
    alternateImageUrl: string;
    contactEmail: string;
    contactPhone: string;
    faviconImageUrl?: string;
    imageUrl: string;
    link: string;
    primaryColorHex: string;
    cnxStorefrontSubHeader?: string;
    cnxStorefrontDisclosuresFooter?: string;
    cnxTermsAndConditionsPreApproval?: string;
    cnxTermsAndConditionsInvitationToApply?: string;
}

export interface IUser {
    access_token: string;
    /**
     * used for oauth2 refresh token posts
     */
    refresh_token?: string;
    /**
     * used to track oauth2 session across token refreshes
     */
    sessionId?: string;
}

export interface LoanFormBase {
    formId: string;
    recordState: string;
    name: string;
    formTemplateType?: string;
    description: string;
    version: number;
    versionDescription: string;
    loanFormTemplateId: string;
    coreProductId: string;
}

export interface LoanForm extends LoanFormBase {
    tenantId: string;
    loanFormTemplateId: string;
    coreProductId: string;
    metadataJSON: string;
    businessRulesJSON: string;
    specificationJSON: string;
    formSpec?: Record<string, unknown>;
    lastUpdated: string;
    coreProduct: CoreProduct;
}

export enum FieldCode {
    ADD_OTHER_BENEFICIARY = 'FIELD-ADD-OTHER-BENEFICIARY',
    ADD_OTHER_EXISTING_COVERAGE = 'FIELD-ADD-OTHER-EXISTING-COVERAGE',
    ADDRESS_MAIL_CITY = 'FIELD-MAIL-ADDR-CITY',
    ADDRESS_MAIL_LINE1 = 'FIELD-MAIL-ADDR-LINE1',
    ADDRESS_MAIL_STATE = 'FIELD-MAIL-ADDR-STATE',
    ADDRESS_MAIL_ZIP = 'FIELD-MAIL-ADDR-ZIP-CODE',
    EDIT_COVERAGE_AMOUNT = 'FIELD-EDIT-COVERAGE-AMOUNT',
    ELECTRONIC_TERMS_AUTHORIZATION = 'FIELD-ELECTRONIC-TERMS-AUTHORIZATION',
    ELECTRONIC_TERMS_CANCEL_APP = 'FIELD-ELECTRONIC-TERMS-CANCEL-APPLICATION',
    ELECTRONIC_TERMS_DOCUMENTS_CONSENT = 'FIELD-ELECTRONIC-TERMS-DOCUMENTS-CONSENT',
    ELECTRONIC_TERMS_DOCUMENTS_LINK = 'FIELD-ELECTRONIC-TERMS-DOCUMENTS-LINK',
    ELECTRONIC_TERMS_PRIVACY_CONSENT = 'FIELD-ELECTRONIC-TERMS-PRIVACY-CONSENT',
    ELECTRONIC_TERMS_PRIVACY_POLICY = 'FIELD-ELECTRONIC-TERMS-PRIVACY-POLICY',
    EMAIL_ADDRESS = 'FIELD-EMAIL-ADDRESS',
    FIRST_NAME = 'FIELD-FIRST-NAME',
    HOME_PHONE = 'FIELD-HOME-PHONE',
    LAST_NAME = 'FIELD-LAST-NAME',
    PAYMENT_DATE_NEW_QUOTE = 'FIELD-USE-PAYMENT-DATE-NEW-QUOTE',
    PAYMENT_ROUTING_NUMBER = 'FIELD-PAYMENT-ROUTING-NUMBER',
    REVIEW_CANCEL_APP = 'FIELD-REVIEW-CANCEL-APPLICATION',
    SECONDARY_ADDRESSEE = 'FIELD-SECONDARY-ADDRESSEE'
}

export enum SegmentCode {
    ADDITIONAL_INFO = 'SEGMENT-ADDITIONAL-INFO',
    BENEFICIARIES = 'SEGMENT-BENEFICIARIES-INFO',
    FINAL_RATE = 'SEGMENT-FINAL-RATE',
    HEALTH_QUESTIONS = 'SEGMENT-HEALTH-QUESTIONS',
    HEALTH_QUESTIONS_CONTINUED = 'SEGMENT-HEALTH-QUESTIONS-CONTINUED',
    PAYMENT_DETAILS = 'SEGMENT-PAYMENT-DETAILS',
    PAYMENT_DETAILS_CC = 'SEGMENT-PAYMENT-DETAILS-CC',
    PERSONAL_INFO = 'SEGMENT-PERSONAL-INFO',
    REVIEW_APP = 'SEGMENT-REVIEW-APPLICATION',
    REVIEW_SUBMIT = 'SEGMENT-REVIEW-AND-SUBMIT',
    COVERAGE_ADJUSTMENT = 'SEGMENT-TL-ADJUST-COVERAGE',
    TERMS_CONDITIONS = 'SEGMENT-TERMS-AND-CONDITIONS'
}

export enum FieldGroupCode {
    ADD_BENEFICIARIES = 'GROUP-ADD-BENEFICIARIES',
    ADD_BENEFICIARIES_HIDDEN = 'GROUP-ADD-BENEFICIARIES-HIDDEN-FIELD',
    ADD_EXISTING_COVERAGE_HIDDEN = 'GROUP-ADD-EXISTING-COVERAGE-HIDDEN-FIELD',
    CONTACT_INFO = 'GROUP-CONTACT-INFO',
    SECONDARY_ADDRESSEE = 'GROUP-SECONDARY-ADDRESSEE-INFO',
    TERMS_CONDITIONS = 'GROUP-TERMS-AND-CONDITIONS'
}

export enum ProductSettingCode {
    ADTRAX = 'FIELD-ADTRAX',
    ADTRAX_BAND1 = 'FIELD-ADTRAX-BAND-1',
    ADTRAX_BAND2 = 'FIELD-ADTRAX-BAND-2',
    APP_DISCLAIMER = 'FIELD-APPLICATION-DISCLAIMER',
    SAFETY_NET_FIRST_LINE1 = 'SAFETY-NET-LOADING-FIRST-MESSAGE-LINE1',
    SAFETY_NET_FIRST_LINE2 = 'SAFETY-NET-LOADING-FIRST-MESSAGE-LINE2',
    SAFETY_NET_SECOND_LINE1 = 'SAFETY-NET-LOADING-SECOND-MESSAGE-LINE1',
    SAFETY_NET_SECOND_LINE2 = 'SAFETY-NET-LOADING-SECOND-MESSAGE-LINE2'
}

export interface InsuranceAppNavResponse {
    insuranceAppId: string;
    forwardEnabled: boolean;
    backEnabled: boolean;
    progress: LoanApplicationEntities.IProgression;
    segment: LoanApplicationEntities.ISectionSegment;
    externalApp: IExternalApp;
}

export interface IExternalApp {
    appId: string;
    appStatus: string;
    appSubStatus: string;
    banners?: IExternalAppBanner[];
    modals?: IExternalAppModal[];
    beneficiaries: IExternalAppSummaryBeneficiaries[];
    billingStatus: string;
    billingFreq: string;
    billingAmount: string;
    coverageAmount: number;
    coverageMaximumAmount?: number;
    coverageMinimumAmount?: number;
    insuredFirstName: string;
    insuredLastName: string;
    insuredFullName: string;
    insuredStatus: string;
    phoneConfirm?: string;
    phoneContact?: string;
    phoneDecline?: string;
    phoneHeader?: string;
    policyNumber?: string;
    productCode: string;
    productMaxCoverageAmount?: number;
    contentAuthoring?: {
        productName?: string;
        productType?: string;
        crossSellModuleTitle?: string;
        finalRateLearnMoreModalTitle?: string;
        finalRateLearnMoreModalSubtitle?: string;
        finalRateLearnMoreModalBody?: string;
    };
    productName?: string;
    productType: string;
    riders?: {
        code: string;
        coverageAmount: number;
        name: string;
        premiumAmount: string;
    }[];
    startAppUnderwritingClass?: string;
    underwritingClass?: string; // Standard, Preferred
    underwritingClassNotification?: boolean;
    underwritingStatus: string;
    finalRatesSectionNumber?: number;
    finalRatesSegmentNumber?: number;
}

export interface IInsuranceAppSegment extends LoanApplicationEntities.INewSectionSegment {
    captchaRequired: boolean;
}

export interface IInsuranceApp extends LoanApplicationEntities.NewLoanApplication {
    externalApp: IExternalApp;
    insuranceAppId: string;
    insuranceForm: {
        formId: string;
        formName: string;
        formTemplateType?: string;
        formVersion: number;
    };
    googleAnalyticsData: IGoogleAnalyticsData;
    sessionId?: string;
    segment: IInsuranceAppSegment;
}

export interface IExternalAppBanner {
    type: string;
    title?: string;
    message?: string;
    dismissable: boolean;
}

export interface IExternalAppModal {
    id: string;
    title?: string;
    body?: string;
}

export interface IExternalAppSummaryBeneficiaries {
    name: {
        firstName: string;
        lastName: string;
    };
    orgName?: string;
    relationship: string;
}

export interface IExternalAppSummary extends IExternalApp {
    emailAddress: string;
    phoneNumber: string;
}

export enum ResultCodes {
    APPROVED = 0,
    PENDING = 10,
    DECLINED = 100,
    DECLINED_GAWL_PIVOT = 1000 /** @todo update with actual code later  */,
    PRIOR_DECLINED = 110,
    BAD_ACTOR = 120, // || 150
    NOT_THE_INSURED = 130,
    BAD_ACTOR_SECONDARY = 150, // Second Bad Actor result code
    NO_REPLACEMENTS = 160,
    STOLI_KNOCKOUT = 170,
    COVERAGE_MAXIMUM = 140,
    RX_FAILURE = 200,
    SESSION_TIMEOUT = 2000 /** @todo update with actual code later  */,
    SYSTEM_UNAVAILABLE = -1,
    IN_PROGRESS = -999,
    PROCESSING_ERRORS = -200,
    PAY_DATE_NEW_PREMIUM = 701
}

export interface IInsuranceAppSummary {
    appId: string;
    coreProductId: string;
    coreProductName: string;
    coreProductType: string;
    coreProductSubType: string;
    coreProductImageUrl?: string;
    coreProductSpecificationJSON?: string;
    formTemplateType?: string;
    externalApp: IExternalAppSummary;
    googleAnalyticsData: IGoogleAnalyticsData;
    recordState?: string;
    resultCode: ResultCodes;
}

export interface IGoogleAnalyticsData {
    addressLine1?: string;
    appSubStatus?: string;
    channel?: string;
    city?: string;
    contNumber?: string;
    coverageAmount?: string;
    correlationId?: string;
    dateOfBirth?: string;
    email?: string;
    firstName?: string;
    gender?: string;
    id?: string;
    insuredStatus?: string;
    issueAge?: string;
    lastName?: string;
    offerNumber?: string;
    offerQuoteId?: string;
    orgName?: string;
    orgNumber?: string;
    paymentFreq?: string;
    paymentMethod?: string;
    phone?: string;
    policyNumber?: string;
    policyStatus?: string;
    premiumAmount?: string;
    productCode?: string;
    state?: string;
    zip?: string;
    partnerCode?: string;
    partnerType?: string;
    eAppId?: string;
}

export interface InsuranceAppStartRequest {
    productCode: string;
    stateAbbr?: string;
    zipCode?: string;
    dateOfBirth?: string;
    sexAtBirth?: string;
    coverage?: number;
    tobaccoUse?: boolean;
    billingMethod?: string;
    billingFrequency?: string;
    contractNumber?: string;
    premium?: Premium[];
    quoteOptions: string;
}

export interface IRider {
    order: number;
    name: string;
    productCode: string;
    coverage: number;
    quoteOptions: QuoteOption[];
    selected: boolean;
    currentIndex: number;
    valuesList: number[];
}

export interface IRiderOption {
    riderCode: string;
    riderDescription: string;
    riderType: string;
    quoteOptions: QuoteOption[];
}

export interface CobrandingPartner {
    name: string;
    displayName: string;
    websiteUrl: string;
    logoUrl: string;
    phoneNumbers: {
        header: string;
        contact: string;
        confirmation: string;
        decline: string;
    };
}
interface FiInfoSuccess {
    name: string;
    addressLine1: string;
    city: string;
    state: string;
    postalCode: string;
    phoneNumber: string;
}
interface FiInfoNotFound {
    type: string;
    title: string;
    status: 404;
    detail: string;
    instance: string;
}
interface FiInfoInvalid {
    type: string;
    title: string;
    status: 400;
    instance: string;
}

export type FiInfo = FiInfoSuccess | FiInfoNotFound | FiInfoInvalid;

export interface QuoteOption {
    monthlyCost: number;
    coverage: number;
    annual: number;
    quarterly: number;
    semiannual: number;
    productCode: string;
}

export enum Frequency {
    MONTHLY = 'Monthly',
    QUARTERLY = 'Quarterly',
    SEMIANNUAL = 'Semiannual',
    ANNUAL = 'Annual'
}

export interface Premium {
    amount: number;
    frequency: Frequency;
}

export interface InputFieldError {
    code: number;
    reason?: string; // optional text reason/meaning of code
    fieldCode?: string;
    fieldMessage?: string;
}

export interface ApiAppError {
    code: number;
    reason?: string;
    redirect: boolean;
    redirectUrl?: string;
    bannerType?: string;
    bannerDismissible?: boolean;
    bannerTitle?: string;
    bannerMessage?: string;
    fieldCode?: string;
    fieldMessage?: string;
    modal?: ApiAppErrorModal;
    inputFieldErrors?: InputFieldError[];
}

export interface ApiAppErrorModal {
    name: string;
    header: string;
    text: string;
}

export interface IGoogleAnalytics {
    fieldName: string;
    eventName: string;
    options: IGoogleAnalyticsOptions[];
    optionsType?: string;
    swapField?: IGoogleAnalyticsSwapField;
}

export interface IGoogleAnalyticsOptions {
    value: string;
    action: string;
    fieldName?: string;
    actionNegate?: string;
}

export interface IGoogleAnalyticsSwapField {
    fieldCode: string;
    value: string;
    swapDisqualifierValue?: string;
}

export enum AnalyticsDataFieldOptionsType {
    CLICK = 'click',
    CONTAINS = 'contains',
    INPUT_STATUS = 'inputStatus',
    LABEL = 'label'
}

export enum ApiErrorBannerType {
    ERROR = 'Error',
    INFO = 'Info',
    WARN = 'Warn'
}

export enum ProductSubType {
    CONSUMER = 'consumer',
    AGENT = 'agent',
    GAWL = 'gawl',
    SIWL = 'siwl',
    TERM = 'term',
    TAWL = 'whole'
}

export enum DLIProductCodes {
    PGA_INSURANCE_2026 = 'PGA Insurance 2026'
}

export enum LifeProductCode {
    GAWL = '2016 GA Whole Life',
    SITERM_BAND1 = '2016 SITERM80',
    /**
     * @todo change to '2020 MSIWLALB' to '2024 MSIWLALB' when quoting (CEQ) for TAWL2.0 is ready
     */
    TAWL = '2020 MSIWLALB'
}

export enum RiderProductCode {
    ACCIDENTAL_DEATH = 'ISRDADB24'
}

export enum AppStatus {
    APPROVED = 'Approved',
    DECLINED = 'Declined',
    IN_PROGRESS = 'In Progress',
    STARTED = 'Started'
}

export enum UnderwritingStatus {
    APPROVED = 'Approved',
    DECLINED = 'Declined',
    INCOMPLETE = 'Incomplete',
    PENDING = 'Pending'
}

export enum UnderwritingClass {
    PREFERRED = 'Preferred'
}

export enum CancelAppReason {
    NO_CONSENT = 'No-Consent',
    NO_RX_PERMISSION = 'No-Rx-Permission'
}

export enum RecordState {
    IN_PROGRESS = 'In Progress'
}

export enum AppExperience {
    DIRECT_TO_CONSUMER = 'DTC',
    AGENT_ASSISTED = 'AA'
}

export enum FormTemplateType {
    DIRECT_TO_CONSUMER = 'DIX-DTC',
    AGENT_ASSISTED = 'DIX-AA'
}

export enum DecisionState {
    Startup = 'Startup', // Initial loading (no poll)
    Loading = 'Loading', // Poll

    // Final states
    Delayed = 'Delayed', // No decision reached
    Resolved = 'Resolved', // Decision reached
    Error = 'Error' // Error
}
